import React from 'react';
import { Hand, MessageCircle, MessageSquareText, Phone, PhoneCall, PhoneForwarded, Undo2 } from 'lucide-react';
import type { LiveOverview, WaitingContact } from '@/services/liveOverviewService';
import type { CallbackTicket } from '@/services/callbackTicketService';
import { formatPhoneDisplay } from '@/lib/transferBanner';
import { ticketPhone } from '@/lib/callbackTicket';
import { NotificationRow, PanelSectionTitle } from './NotificationRow';
import {
  displayName,
  elapsedSince,
  formatAgentTakesOver,
  formatClock,
  formatRelative,
  formatReplyWindow,
  formatSince,
  formatWait,
} from './notificationFormat';

interface LiveNowSectionProps {
  overview: LiveOverview;
  /** Server-clock ms, ticking every second while the panel is open. */
  now: number;
  onOpenContact: (contactKey: string) => void;
  onOpenCall: (callSid: string) => void;
  /** Opens the call of a Rückruf-Ticket. */
  onOpenTicket: (ticket: CallbackTicket) => void;
  /** Several agent numbers in use: say which one the customer wrote to. */
  showAgentNumber: boolean;
}

const join = (...parts: Array<string | null | undefined | false>) => parts.filter(Boolean).join(' · ');

function viaAgent(agentNumber: string | null, show: boolean): string | null {
  return show && agentNumber ? `über ${formatPhoneDisplay(agentNumber)}` : null;
}

function WaitingRow({
  entry,
  now,
  onOpen,
  showAgentNumber,
}: {
  entry: WaitingContact;
  now: number;
  onOpen: () => void;
  showAgentNumber: boolean;
}) {
  const name = displayName(entry.customer_name, entry.customer_phone);
  const via = viaAgent(entry.agent_number, showAgentNumber);

  if (entry.state === 'callback') {
    const replyWindow = formatReplyWindow(entry.reply_window_until, now);
    const phone = entry.customer_phone ? formatPhoneDisplay(entry.customer_phone) : '';
    return (
      <NotificationRow
        icon={Undo2}
        urgent
        title={`${name} wartet auf Rückmeldung`}
        detail={join(formatSince(entry.waiting_since, now), entry.reason, replyWindow?.text, via)}
        onClick={onOpen}
        aside={
          replyWindow?.expired && phone ? (
            <a
              href={`tel:${phone}`}
              className="rounded-md px-2 py-1 text-xs font-semibold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              {phone}
            </a>
          ) : undefined
        }
      />
    );
  }

  const waited = formatWait(elapsedSince(entry.waiting_since, now));
  const takesOver = formatAgentTakesOver(entry.deadline_at, now);
  if (entry.state === 'requested') {
    return (
      <NotificationRow
        icon={Hand}
        urgent
        title={`${name} möchte einen Mitarbeiter`}
        detail={join(entry.reason, `wartet ${waited}`, takesOver, via)}
        onClick={onOpen}
      />
    );
  }
  return (
    <NotificationRow
      icon={MessageCircle}
      urgent
      title={`${name} wartet auf deine Antwort`}
      detail={join(`wartet ${waited}`, takesOver, via)}
      onClick={onOpen}
    />
  );
}

/** A caller who left a Rückruf-Ticket: the row opens the call, the number dials. */
function TicketRow({
  ticket,
  now,
  onOpen,
  showAgentNumber,
}: {
  ticket: CallbackTicket;
  now: number;
  onOpen: () => void;
  showAgentNumber: boolean;
}) {
  const phone = ticketPhone(ticket);
  return (
    <NotificationRow
      icon={PhoneCall}
      urgent
      title={`${displayName(ticket.customer_name, ticket.customer_phone)} wartet auf Rückruf`}
      detail={join(
        formatSince(ticket.created_at, now),
        ticket.reason,
        ticket.preferred_window ? `Wunsch: ${ticket.preferred_window}` : null,
        viaAgent(ticket.agent_number, showAgentNumber),
      )}
      onClick={onOpen}
      aside={
        phone ? (
          <a
            href={`tel:${phone}`}
            aria-label={`Anrufen: ${phone}`}
            className="rounded-md px-2 py-1 text-xs font-semibold text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
          >
            Anrufen
          </a>
        ) : undefined
      }
    />
  );
}

/** „Jetzt live“: waiting customers first (WhatsApp, then phone callbacks), then running calls, then chats with the bot. */
export function LiveNowSection({
  overview,
  now,
  onOpenContact,
  onOpenCall,
  onOpenTicket,
  showAgentNumber,
}: LiveNowSectionProps) {
  const waitingKeys = new Set(overview.waiting.map((w) => w.contact_key));
  const chats = overview.active_chats.filter((c) => !waitingKeys.has(c.contact_key));
  const count =
    overview.waiting.length + overview.callback_tickets.length + overview.live_calls.length + chats.length;
  if (count === 0) return null;

  return (
    <section aria-labelledby="bell-live-title">
      <PanelSectionTitle id="bell-live-title">Jetzt live</PanelSectionTitle>
      <ul className="space-y-0.5" aria-labelledby="bell-live-title">
        {overview.waiting.map((entry) => (
          <WaitingRow
            key={`w-${entry.contact_key}`}
            entry={entry}
            now={now}
            onOpen={() => onOpenContact(entry.contact_key)}
            showAgentNumber={showAgentNumber}
          />
        ))}
        {overview.callback_tickets.map((ticket) => (
          <TicketRow
            key={`t-${ticket.id}`}
            ticket={ticket}
            now={now}
            onOpen={() => onOpenTicket(ticket)}
            showAgentNumber={showAgentNumber}
          />
        ))}
        {overview.live_calls.map((call) => {
          const who = displayName(call.customer_name, call.customer_phone);
          const transferring = call.status === 'transferring';
          return (
            <NotificationRow
              key={`c-${call.call_sid}`}
              icon={transferring ? PhoneForwarded : Phone}
              title={`${transferring ? 'Wird weitergeleitet' : 'Anruf läuft'} · ${who}`}
              detail={join(
                formatClock(elapsedSince(call.started_at, now)),
                call.last_step ? `Agent ${call.last_step}` : null,
                'mitlesen',
                viaAgent(call.agent_number, showAgentNumber),
              )}
              onClick={() => onOpenCall(call.call_sid)}
            />
          );
        })}
        {chats.map((chat) => (
          <NotificationRow
            key={`a-${chat.contact_key}`}
            icon={MessageSquareText}
            title={`${displayName(chat.customer_name, chat.customer_phone)} schreibt mit dem Agenten`}
            detail={join(chat.preview ? `„${chat.preview}“` : null, formatRelative(chat.last_message_at, now))}
            onClick={() => onOpenContact(chat.contact_key)}
          />
        ))}
      </ul>
    </section>
  );
}

export default LiveNowSection;
